import { API_BASE_URL, STORAGE_KEYS } from '../constants';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    console.log('=== API REQUEST ===');
    console.log('URL:', url);
    console.log('Method:', options.method || 'GET');
    console.log('Headers:', {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    });
    console.log('Body:', options.body);
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log('Making request to:', url);
      const response = await fetch(url, config);
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        this.clearToken();
        // No redirigir automáticamente, dejar que el componente maneje el error
        throw new Error('Unauthorized');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('Response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('Network error:', error);
      
      // Manejar diferentes tipos de errores de red
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: No se pudo conectar con el servidor');
      } else if (error.name === 'AbortError') {
        throw new Error('Network error: La solicitud fue cancelada');
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Error de conexión con el servidor');
      } else if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Network error: Error de conexión');
      }
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  getToken() {
    return this.token;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Auth service
export const authService = {
  async login(credentials) {
    console.log('=== AUTH SERVICE LOGIN ===');
    console.log('Credentials:', credentials);
    
    try {
      const response = await apiClient.post('/api/auth/login/', credentials);
      console.log('Login response:', response);
      
      if (response.success) {
        console.log('Login successful, setting token and user');
        apiClient.setToken(response.token);
        authService.setCurrentUser(response.user);
      } else {
        console.log('Login failed:', response.message);
      }
      return response;
    } catch (error) {
      console.error('Login error in authService:', error);
      console.error('Error message:', error.message);
      throw error;
    }
  },

  async logout() {
    try {
      await apiClient.post('/api/auth/logout/');
    } finally {
      apiClient.clearToken();
    }
  },

  async getCurrentUser() {
    try {
      const response = await apiClient.get('/api/auth/user/');
      if (response.success) {
        authService.setCurrentUser(response.user);
        return response.user;
      }
    } catch (error) {
      console.error('Error getting user info:', error);
    }
    return null;
  },

  setCurrentUser(user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },

  getStoredUser() {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  },

  async register(userData) {
    const response = await apiClient.post('/api/auth/register/', userData);
    if (response.success) {
      apiClient.setToken(response.token);
      authService.setCurrentUser(response.user);
    }
    return response;
  },

  async changePassword(passwordData) {
    const response = await apiClient.post('/api/auth/change-password/', passwordData);
    return response;
  },

  async forgotPassword(email) {
    const response = await apiClient.post('/api/auth/forgot-password/', { email });
    return response;
  },

  async resetPassword(resetData) {
    const response = await apiClient.post('/api/auth/reset-password/', resetData);
    return response;
  },
};